import React, { useEffect, useState, useCallback } from 'react';
import { Modal, ModalBody } from '../common';

interface FeedbackModalProps {
  /** Unique ID for this prompt (used for session storage) */
  promptId: string;
  /** URL to submit feedback */
  feedbackUrl: string;
  /** URL to postpone feedback */
  postponeUrl: string;
  /** Google Play Store URL for reviews */
  googlePlayUrl: string;
  /** CSRF token for API requests */
  csrfToken: string;
  /** Translations for modal content */
  translations: {
    title: string;
    subtitle: string;
    ratingQuestion: string;
    pleaseSelectRating: string;
    highRatingTitle: string;
    highRatingText: string;
    reviewButton: string;
    commentsPlaceholder: string;
    commentsHelp: string;
    submit: string;
    postpone: string;
  };
}

/**
 * FeedbackModal - Collects user feedback with star rating and comments
 *
 * Ported from:
 * - app/views/diaries/shared/_feedback_modal.html.erb
 * - app/javascript/controllers/feedback_modal_controller.js
 */
export function FeedbackModal({
  promptId,
  feedbackUrl,
  postponeUrl,
  googlePlayUrl,
  csrfToken,
  translations,
}: FeedbackModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [comments, setComments] = useState('');
  const [showRatingError, setShowRatingError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check session storage to avoid showing the same modal twice
  useEffect(() => {
    const sessionKey = `feedback_modal_shown_${promptId}`;

    if (sessionStorage.getItem(sessionKey)) {
      return;
    }

    sessionStorage.setItem(sessionKey, 'true');
    setIsOpen(true);
  }, [promptId]);

  const handleSelectRating = useCallback((rating: number) => {
    setSelectedRating(rating);
    setShowRatingError(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!selectedRating) {
        setShowRatingError(true);
        return;
      }

      setIsSubmitting(true);

      try {
        const formData = new FormData();
        formData.append('rating', selectedRating.toString());
        formData.append('comments', comments);

        const response = await fetch(feedbackUrl, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
            Accept: 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
          },
          body: formData,
        });

        if (response.ok) {
          setIsOpen(false);
        }
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedRating, comments, feedbackUrl, csrfToken]
  );

  const handlePostpone = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();

      try {
        const response = await fetch(postponeUrl, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
            Accept: 'text/vnd.turbo-stream.html',
          },
        });

        if (response.ok) {
          setIsOpen(false);
        }
      } catch (error) {
        console.error('Failed to postpone feedback:', error);
      }
    },
    [postponeUrl, csrfToken]
  );

  // This modal should not close on backdrop click or escape
  const handleClose = useCallback(() => {
    // Do nothing - this modal requires explicit action
  }, []);

  if (!isOpen) {
    return null;
  }

  const showHighRatingMessage = selectedRating !== null && selectedRating >= 4;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnBackdropClick={false}
      closeOnEscape={false}
      ariaLabel={translations.title}
      centered
    >
      <ModalBody className="p-0">
        <div
          className="p-5"
          style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
        >
          <div className="text-center mb-4">
            <div className="d-inline-block position-relative mb-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center bg-white mx-auto"
                style={{ width: 100, height: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
              >
                <i
                  className="bi bi-star-fill"
                  style={{
                    fontSize: '3rem',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                ></i>
              </div>
            </div>
            <h4 className="mb-2 fw-bold text-white" style={{ fontSize: '1.75rem' }}>
              {translations.title}
            </h4>
            <p className="text-white text-opacity-90 mb-0">{translations.subtitle}</p>
          </div>

          <div className="mb-4">
            <label className="form-label fw-semibold text-center d-block mb-3 text-white">
              {translations.ratingQuestion}
            </label>
            <div className="d-flex justify-content-center gap-3">
              {[1, 2, 3, 4, 5].map((rating) => {
                const isSelected = selectedRating !== null && rating <= selectedRating;
                return (
                  <button
                    key={rating}
                    type="button"
                    className={`btn star-rating-btn p-3 rounded-circle ${isSelected ? 'btn-warning' : 'btn-light'}`}
                    style={{
                      width: 60,
                      height: 60,
                      border: isSelected ? '3px solid #ffc107' : '3px solid rgba(255,255,255,0.3)',
                      transition: 'all 0.3s ease',
                      transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                    }}
                    onClick={() => handleSelectRating(rating)}
                    data-testid={`rating-${rating}`}
                  >
                    <i className="bi bi-star-fill fs-4" style={{ color: '#ffd700' }}></i>
                  </button>
                );
              })}
            </div>
            <div className="text-center mt-2">
              {showRatingError && (
                <small className="text-white text-opacity-75">
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {translations.pleaseSelectRating}
                </small>
              )}
            </div>
          </div>

          {showHighRatingMessage && (
            <div className="mb-4">
              <div className="alert alert-light d-flex align-items-start border-0 shadow-sm">
                <i className="bi bi-heart-fill me-2 mt-1 text-danger"></i>
                <div>
                  <strong>{translations.highRatingTitle}</strong>
                  <p className="mb-1 small mt-1">{translations.highRatingText}</p>
                  <a href={googlePlayUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-success mt-2">
                    <i className="bi bi-google-play me-1"></i>
                    {translations.reviewButton}
                  </a>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <textarea
                className="form-control border-0 shadow-sm"
                style={{ background: 'white', minHeight: 120 }}
                rows={4}
                placeholder={translations.commentsPlaceholder}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                data-testid="feedback-comments"
              />
              <small className="form-text text-white text-opacity-75">{translations.commentsHelp}</small>
            </div>

            <div className="d-grid gap-2">
              <button
                type="submit"
                className="btn btn-light fw-bold py-3"
                style={{ fontSize: '1.1rem' }}
                disabled={!selectedRating || isSubmitting}
                data-testid="submit-feedback"
              >
                {translations.submit}
              </button>

              <button
                type="button"
                className="btn btn-outline-light"
                onClick={handlePostpone}
                data-testid="postpone-feedback"
              >
                {translations.postpone}
              </button>
            </div>
          </form>
        </div>
      </ModalBody>
    </Modal>
  );
}
